/**
 * The reference never plays into an open microphone (Sobhan, 2026-10-16:
 * on a quick repetition the woman's voice started with the take, the live
 * text heard *her*, and the repetition counted without the learner saying a
 * word).
 *
 * Why the old guards missed it: the replay between repetitions is scheduled
 * after the cue, and it checked whether the microphone was recording — a
 * value React only updates a render after the tap, once the microphone has
 * actually opened. A tap just before the replay passed both checks, and the
 * replay then played the whole take long.
 *
 * So the practice holds one guard, changed synchronously by the tap itself:
 * - `arm()` the moment a take is asked for — before the microphone opens —
 *   pausing every watched reference element and voiding every replay
 *   planned before it;
 * - `disarm()` once the take has ended (or never started);
 * - `watch(element)`: a play that starts on a watched element while armed —
 *   a pending autoplay, a late replay, the element's own controls — is
 *   undone at once;
 * - `plan()` / `mayReplay(token)`: a replay planned before a take never runs.
 */
export function createReferenceGuard() {
  let recording = false
  let epoch = 0
  const watched = new Map()

  function pauseAll() {
    for (const element of watched.keys()) element.pause?.()
  }

  return {
    get recording() {
      return recording
    },
    arm() {
      recording = true
      epoch += 1
      pauseAll()
    },
    disarm() {
      recording = false
    },
    /** A token for a replay decided now. */
    plan() {
      return epoch
    },
    /** False while a take is on, and for good once one has started since `token` was planned. */
    mayReplay(token) {
      return !recording && token === epoch
    },
    watch(element) {
      if (!element || watched.has(element)) return
      const onPlay = () => {
        if (recording) element.pause?.()
      }
      element.addEventListener?.('play', onPlay)
      element.addEventListener?.('playing', onPlay)
      watched.set(element, onPlay)
    },
    unwatch(element) {
      const onPlay = watched.get(element)
      if (!onPlay) return
      element.removeEventListener?.('play', onPlay)
      element.removeEventListener?.('playing', onPlay)
      watched.delete(element)
    },
  }
}
